import type { EnrollmentValidationError } from "@repo/models";

import { styles } from "@repo/helpers";
import { MoveableDialog } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { RepPortalTabs } from "agent-portal";
import { useEffect, useState } from "react";

import type { EnrollmentValidationFilter } from "./types";

import EnrollmentValidationApi, { controller } from "../../api/enrollmentValidationApi";
import { QUERY_KEYS } from "../../api/queryKeys";
import { AgentPortal } from "../../apps/agentPortal";
import { TLPGrid } from "../../components/tlpGrid";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import { useGridColumns } from "../../hooks/useGridColumns";
import { EnrollmentValidationReviewForm } from "./reviewForm";
import style from "./style.module.scss";

interface Props {
	filters: EnrollmentValidationFilter;
	setFilters: (filters: EnrollmentValidationFilter) => void;
	isAdmin?: boolean;
}

export function EnrollmentValidationGrid(props: Props) {
	const [portalOpen, setPortalOpen] = useState(false);
	const [reviewOpen, setReviewOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<EnrollmentValidationError | null>(null);
	const [selectedTab, setSelectedTab] = useState<RepPortalTabs | null>(null);

	useEffect(() => {
		if (reviewOpen) return;
		if (portalOpen) return;
		if (selectedItem == null) return;

		setSelectedTab(null);
		setSelectedItem(null);
	}, [reviewOpen, portalOpen, selectedItem, setSelectedItem, setSelectedTab]);

	const validationsQuery = useQuery({
		queryKey: [QUERY_KEYS.EnrollmentValidations.Grid, props.filters],
		queryFn: () => EnrollmentValidationApi.getEnrollmentValidations(props.filters),
	});

	const openReviewModal = (item: EnrollmentValidationError) => {
		setSelectedItem(item);
		setReviewOpen(true);
	};

	const handleReviewSaved = async (action: "next" | "open" | "close") => {
		const refetched = await validationsQuery.refetch();

		if (action === "close") {
			setReviewOpen(false);
			return;
		}

		if (action === "open") {
			setReviewOpen(false);
			setSelectedTab("EnrollmentValidations");
			setPortalOpen(true);
			return;
		}

		//Walk the list we were reviewing - saving can filter the record out of the refreshed
		//list, which would otherwise make "next" look like the end of the list
		const reviewedItems = validationsQuery.data?.items ?? [];
		const currentIndex = reviewedItems.findIndex((i) => i.id === selectedItem?.id);
		const nextItem = currentIndex >= 0 ? reviewedItems[currentIndex + 1] : undefined;
		if (!nextItem) {
			setReviewOpen(false);
			return;
		}

		//Prefer the refreshed copy of that record so the form opens on current data
		const refreshedNext = refetched.data?.items?.find((i) => i.id === nextItem.id);
		setSelectedItem(refreshedNext ?? nextItem);
	};

	const openPortalModal = (item: EnrollmentValidationError) => {
		setSelectedItem(item);
		setSelectedTab("EnrollmentValidations");
		setPortalOpen(true);
	};

	const openIncidentModal = (item: number) => {
		const selected = validationsQuery.data.items.find((i) => i.complaintId === item);

		setSelectedItem(selected);
		setSelectedTab("Complaints");
		setPortalOpen(true);
	};

	const { columns, isLoading: isLoadingColumns } = useGridColumns(
		"EnrollmentValidation",
		EnrollmentValidationApi.getGridColumns,
		{ openPortalModal, openReviewModal, openIncidentModal },
	);

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<EnrollmentValidationError, EnrollmentValidationFilter>
				id="enrollmentValidationsGrid"
				title="Enrollment Validations"
				gridNameForExport="EnrollmentValidations"
				pageSizes={[20, 50, 100]}
				keyProperty="id"
				filters={props.filters}
				setFilters={props.setFilters}
				query={validationsQuery}
				columns={columns}
				apiRoute={controller}
				gridApi={EnrollmentValidationApi}
				exportFunc={EnrollmentValidationApi.getEnrollmentValidations}
				selected={selectedItem?.id ? [selectedItem.id] : []}
			/>
			<MoveableDialog
				open={portalOpen}
				onOpenChange={setPortalOpen}
				title="Agent Portal"
				initialSize={getInitialDialogSize()}
				resizable
				movable
				className={styles("agent-portal-modal", style.noPadding)}
				contentClassName={style.noPadding}
			>
				{selectedItem && (
					<AgentPortal
						repId={selectedItem.representativeId}
						selectedTab={selectedTab}
						entityId={selectedTab === "EnrollmentValidations" ? selectedItem.id : selectedItem.complaintId}
					/>
				)}
			</MoveableDialog>
			{selectedItem ? (
				<EnrollmentValidationReviewForm
					key={selectedItem.id}
					open={reviewOpen}
					onOpenChange={setReviewOpen}
					item={selectedItem}
					onSaved={handleReviewSaved}
				/>
			) : null}
		</>
	);
}
